/**
 * DataTable — багана тодорхойлолтоос хүснэгт бүтээнэ.
 *
 * ЯАГААД: өмнө нь хуудас бүр `<table>`-ээ гараар бичдэг байсан тул
 * hover, хүрээ, тоон баганын байрлал хуудас болгонд өөр байв. Одоо
 * зөвхөн багана тодорхойлохоос өөр зүйл шаардахгүй.
 *
 * МОБАЙЛ: 640px-ээс доош хүснэгт БАЙХГҮЙ — карт болж хувирна.
 * Хэвтээ гүйдэг хүснэгтэд ажилтан гар утсан дээр багана хайж
 * олдоггүй. Хоёр DOM зэрэг үүсдэг ч мөрийн тоо арав, хорь тул
 * үнэ нь бага.
 *
 * ТОО БАРУУН, ТЕКСТ ЗҮҮН — `align: 'right'` нь `data-num` шошго
 * тавьж `font-variant-numeric: tabular-nums` идэвхжүүлнэ.
 */

import type { ReactNode } from 'react';
import { SkeletonTable } from './skeleton';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Тоон багана баруун тийш, tabular-nums-тай */
  align?: 'left' | 'right';
  /** Мөр таслахгүй (огноо, код, серийн дугаар) */
  nowrap?: boolean;
  /** `<th>`-д нэмэх өргөний class, ж: 'w-[170px]' */
  width?: string;
  /**
   * Мобайл картад эзлэх үүрэг.
   *   title — картын гарчиг (эхний баганын анхдагч)
   *   value — баруун дээд талын гол утга
   *   meta  — шошго/утга хос болж доор жагсана (анхдагч)
   *   hide  — мобайлд огт харагдахгүй
   */
  mobile?: 'title' | 'value' | 'meta' | 'hide';
}

interface Props<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  /** Сондгой мөрийг бүдэгрүүлнэ — олон баганатай хүснэгтэд нүд алдахгүй */
  zebra?: boolean;
  /**
   * Мөрийн төлөвөөс хамаарсан class (ж: архивлагдсаныг `opacity-50`).
   * Хүснэгтийн мөр ба мобайл картад ХОЁУЛАНД нь тавигдана.
   */
  rowClass?: (row: T) => string;
  /**
   * Толгойг наалдуулна. Хүснэгт өөрөө босоо гүйдэг бүс болно
   * (`max-h`) — эс тэгвээс sticky ажиллах орон зайгүй.
   */
  sticky?: boolean;
  loading?: boolean;
  /** Мөр байхгүй үед — `EmptyState` дамжуулна */
  empty?: ReactNode;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  zebra = false,
  rowClass,
  sticky = false,
  loading = false,
  empty,
}: Props<T>) {
  if (loading) return <SkeletonTable rows={5} cols={Math.min(columns.length, 5)} />;
  if (rows.length === 0) return <>{empty}</>;

  return (
    <>
      {/* ── Мобайл: карт жагсаалт ────────────────────────────── */}
      <ul className="flex flex-col gap-2 sm:hidden">
        {rows.map((row, i) => (
          <MobileCard
            key={rowKey(row, i)}
            row={row}
            columns={columns}
            onClick={onRowClick}
            className={rowClass?.(row) ?? ''}
          />
        ))}
      </ul>

      {/* ── Таблет ба дэсктоп: жинхэнэ хүснэгт ───────────────── */}
      <div
        className={`hidden sm:block ${
          sticky ? 'table-sticky max-h-[70dvh] overflow-auto' : 'overflow-x-auto'
        }`}
      >
        <table className="w-full border-collapse text-body">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  {...(c.align === 'right' ? { 'data-num': 'true' } : {})}
                  className={`border-b border-hair bg-well-2 px-3 py-2 text-label font-medium
                    text-ink-2 uppercase ${c.align === 'right' ? 'text-right' : 'text-left'}
                    ${c.nowrap || c.align === 'right' ? 'whitespace-nowrap' : ''} ${c.width ?? ''}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={rowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`transition hover:bg-row-hover ${zebra ? 'odd:bg-zebra' : ''} ${
                  onRowClick ? 'cursor-pointer' : ''
                } ${rowClass?.(row) ?? ''}`}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    {...(c.align === 'right' ? { 'data-num': 'true' } : {})}
                    className={`border-b border-hair/70 px-3 py-2.5
                      ${c.align === 'right' ? 'text-right whitespace-nowrap' : ''}
                      ${c.nowrap ? 'whitespace-nowrap' : ''}`}
                  >
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Нэг мөр = нэг карт. Гарчиг дээр, гол утга баруун, бусад нь доор. */
function MobileCard<T>({
  row,
  columns,
  onClick,
  className = '',
}: {
  row: T;
  columns: Column<T>[];
  onClick?: (row: T) => void;
  className?: string;
}) {
  const title = columns.find((c) => c.mobile === 'title') ?? columns[0];
  const value = columns.find((c) => c.mobile === 'value');
  const meta = columns.filter(
    (c) => c !== title && c !== value && c.mobile !== 'hide',
  );

  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        {/* Кирилл нэр урт — таслахгүй, боож өгнө */}
        <span className="min-w-0 flex-1 text-body font-medium text-pretty">
          {title ? title.cell(row) : null}
        </span>
        {value ? (
          <span className="num shrink-0 text-body font-medium whitespace-nowrap">
            {value.cell(row)}
          </span>
        ) : null}
      </div>

      {meta.length > 0 ? (
        <dl className="flex flex-col gap-1">
          {meta.map((c) => (
            <div key={c.key} className="flex items-baseline justify-between gap-3">
              <dt className="shrink-0 text-micro text-ink-3 uppercase">{c.header}</dt>
              <dd
                {...(c.align === 'right' ? { 'data-num': 'true' } : {})}
                className="min-w-0 text-right text-meta text-ink-2"
              >
                {c.cell(row)}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </>
  );

  const cls =
    'flex w-full flex-col gap-2 rounded-well border border-hair bg-panel px-3.5 py-3 ' +
    `text-left ${className}`;

  return (
    <li>
      {onClick ? (
        <button type="button" onClick={() => onClick(row)} className={`${cls} transition active:bg-row-hover`}>
          {inner}
        </button>
      ) : (
        <div className={cls}>{inner}</div>
      )}
    </li>
  );
}
